import { create } from "zustand";
import { Project } from "@/backend/models/project";
import { ProjectFrontendController } from "../controllers/projectFrontendController";
import { PartFrontendController } from "../controllers/partFrontendController";
import { CreateProjectRequest, CreateProjectFromTemplateRequest, ProjectResponse } from "@/types/projectTypes";
import { PartParams } from "@/backend/access/part/types";
import { PartResponse, CreatePartRequest, CreatePartFromTemplateRequest } from "@/backend/express/controllers/partController";
import { PartDutAssignmentsFrontendController } from "../controllers/partDutAssignmentsFrontendController";
import { ProjectProcedureInstanceAssignmentController } from "../controllers/projectProcedureInstanceAssignmentController";
import { ProjectProcedureInstanceAssignmentParams } from "@/backend/access/projectProcedureInstanceAssignment/types";
import { useProjectLockStore } from "./ProjectLockStore";
import { Requirement } from "@/backend/models/requirement";
import { I18n } from "@/i18n/I18n";
import { FusaToastType } from "../toasts/types";
import { useGlobalStore } from "./GlobalStore";

const initialState: { projects: Project[]; recentProjects: Project[]; projectRequirements: Record<string, Requirement[]>; projectParts: PartParams[]; projectProcedureInstanceAssignments: ProjectProcedureInstanceAssignmentParams[] } = {
  projects: [],
  recentProjects: [],
  projectRequirements: {},
  projectParts: [],
  projectProcedureInstanceAssignments: [],
};

export interface ProjectStore {
  projects: Project[];
  recentProjects: Project[];
  projectParts: PartParams[];
  projectProcedureInstanceAssignments: ProjectProcedureInstanceAssignmentParams[];
  fetchProject: (id: string) => Promise<void>;
  fetchAllProjects: () => Promise<void>;
  fetchAllProjectsForAdmin: () => Promise<void>;
  fetchRecentProjects: () => Promise<void>;
  recordProjectView: (id: string) => Promise<void>;
  fetchProjectsByParentId: (parentId: string) => Promise<void>;
  setProjectParts: (parts: PartParams[]) => void;
  fetchProjectParts: (projectId: string) => Promise<void>;
  fetchPartsForProcedureInstance: (procedureInstanceId: string) => Promise<void>;
  fetchPartsForProcedureInstances: (procedureInstanceIds: string[]) => Promise<void>;
  reset: () => void;
  createProject: (project: CreateProjectRequest) => Promise<ProjectResponse>;
  createProjectFromTemplate: (data: CreateProjectFromTemplateRequest) => Promise<void>;
  updateProject: (project: Project) => Promise<ProjectResponse>;
  deleteProject: (id: string) => Promise<void>;
  refetchProject: (id: string) => Promise<void>;
  createPart: (part: CreatePartRequest) => Promise<PartResponse>;
  createPartFromTemplate: (data: CreatePartFromTemplateRequest) => Promise<PartResponse>;
  deletePart: (projectId: string, partId: string) => Promise<boolean>;
  readPart: (partId: string) => Promise<PartResponse>;
  fetchAssignmentsByProjectId: (projectId: string) => Promise<void>;
  createProjectAssignment: (projectId: string, procedureInstanceId: string) => Promise<void>;
  deleteProjectAssignment: (projectId: string, assignmentId: string) => Promise<void>;
  removeAssignmentsForProcedure: (procedureInstanceId: string) => void;
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  reset: () => set({ ...initialState }),
  projects: initialState.projects,
  recentProjects: initialState.recentProjects,
  projectParts: initialState.projectParts,
  projectProcedureInstanceAssignments: initialState.projectProcedureInstanceAssignments,
  fetchAllProjects: async () => {
    const response = await new ProjectFrontendController().getAllProjects();
    if (response.status === 200) {
      set({ projects: response.projects.map((project) => new Project(project)) });
      await useProjectLockStore.getState().fetchProjectLocksByProjectIds(response.projects.map(project => project.id));
    }
  },
  fetchAllProjectsForAdmin: async () => {
    const response = await new ProjectFrontendController().getAllProjectsForAdmin();
    if (response.status === 200) {
      set({ projects: response.projects.map((project) => new Project(project)) });
    }
  },
  // Kept out of `projects`, which sibling pages replace wholesale and `fetchProject` appends to.
  fetchRecentProjects: async () => {
    const response = await new ProjectFrontendController().getRecentProjects();
    if (response.status === 200) {
      set({ recentProjects: response.projects.map((project) => new Project(project)) });
      await useProjectLockStore.getState().fetchProjectLocksByProjectIds(response.projects.map(project => project.id));
    }
  },
  recordProjectView: async (id: string) => {
    await new ProjectFrontendController().recordProjectView(id);
  },
  fetchProjectsByParentId: async (parentId: string) => {
    const response = await new ProjectFrontendController().getProjectsByParentId(parentId);
    if (response.status === 200) {
      set({ projects: response.projects.map((project) => new Project(project)) });
      await useProjectLockStore.getState().fetchProjectLocksByProjectIds(response.projects.map(project => project.id));
    }
  },
  setProjectParts: (parts: PartParams[]) => 
    set({ projectParts: parts }),
  createProject: async (project: CreateProjectRequest) => {
    const response = await new ProjectFrontendController().createProject(project);

    if (response.status === 201 && response.project) {
      const readableProject = new Project(response.project);
      set({ projects: [...get().projects, readableProject] });
    }
    return response;
  },
  createProjectFromTemplate: async (data: CreateProjectFromTemplateRequest) => {
    const response = await new ProjectFrontendController().createProjectFromTemplate(data);

    if (response.status === 201 && response.project) {
      const readableProject = new Project(response.project);
      set({ projects: [...get().projects, readableProject] });
      useGlobalStore.getState().addToast({
        type: FusaToastType.SUCCESS,
        message: I18n.get('projectCreated'),
        link: `/projects/${readableProject.id}`,
        linkText: readableProject.title,
        header: I18n.get('success'),
        autohide: true
      });
    }
  },
  updateProject: async (project: Project) => {
    const response = await new ProjectFrontendController().updateProject(project);

    if (!response.errors && response.project) {
      const replaceUpdated = (p: Project) =>
        p.id === response.project?.id ? new Project(response.project) : p;
      set({
        projects: get().projects.map(replaceUpdated),
        recentProjects: get().recentProjects.map(replaceUpdated),
      });
    }
    return response;
  },
  deleteProject: async (id: string) => {
    const response = await new ProjectFrontendController().deleteProject(id);

    if (response.status === 200) {
      set({
        projects: get().projects.filter((project) => project.id !== id),
        recentProjects: get().recentProjects.filter((project) => project.id !== id),
      });
    }
  },
  fetchProject: async (id: string) => {
    const response = await new ProjectFrontendController().readProject(id);
    if (response.status === 200) {
      set({ projects: [...get().projects, new Project(response.project!)] });
    }
    await useProjectLockStore.getState().fetchProjectLocksByProjectIds([id]);
  },
  refetchProject: async (id: string) => {
    const response = await new ProjectFrontendController().readProject(id);

    if (!response.errors && response.project) {
      const replaceRefetched = (p: Project) => p.id === id ? new Project(response.project!) : p;
      set({
        projects: get().projects.map(replaceRefetched),
        recentProjects: get().recentProjects.map(replaceRefetched),
      });
    }
  },
  fetchProjectParts: async (projectId: string) => {
    const response = await new PartFrontendController().getAllPartsByProject(projectId);
    if (response.status === 200) {
      const otherParts = get().projectParts.filter(p => p.projectId !== projectId);
      set({ projectParts: [...otherParts, ...response.parts] });
    }
  },
  fetchPartsForProcedureInstance: async (procedureInstanceId: string) => {
    const response = await new PartDutAssignmentsFrontendController().getPartDutAssignmentsByProcedureInstanceId(procedureInstanceId);
    if (response.status === 200) {
      response.partDutAssignments.map(pda => void get().readPart(pda.partId));
    }
  },
  fetchPartsForProcedureInstances: async (procedureInstanceIds: string[]) => {
    const response = await new PartDutAssignmentsFrontendController().getPartDutAssignmentsByProcedureInstanceIds(procedureInstanceIds);
    if (response.status === 200) {
      response.partDutAssignments.map(pda => void get().readPart(pda.partId));
    }
  },
  createPart: async (part: CreatePartRequest) => {
    const response = await new PartFrontendController().createPart(part);

    if (response.status === 201 && response.part) {
      set({ projectParts: [...get().projectParts, response.part] });
    }
    return response;
  },
  createPartFromTemplate: async (data: CreatePartFromTemplateRequest) => {
    const response = await new PartFrontendController().createPartFromTemplate(data);

    if (response.status === 201 && response.part) {
      set({ projectParts: [...get().projectParts, response.part] });
    }
    return response;
  },
  deletePart: async (projectId: string, partId: string) => {
    const response = await new PartFrontendController().deletePart(partId);

    if (response.status === 200) {
      set({ projectParts: get().projectParts.filter((p) => p.id !== partId) });
      return true;
    }
    return false;
  },
  readPart: async (partId: string) => {
    const response = await new PartFrontendController().getPart(partId);
    
    if (response.status === 200 && response.part) {
      const partExists = get().projectParts.some(p => p.id === partId);
      if (!partExists) {
        set({ projectParts: [...get().projectParts, response.part] });
      } else {
        // Update existing part
        const updatedParts = get().projectParts.map(p => p.id === partId ? response.part! : p);
        set({ projectParts: updatedParts });
      }
    }
    
    return response;
  },
  fetchAssignmentsByProjectId: async (projectId: string) => {
    const response = await new ProjectProcedureInstanceAssignmentController().getAssignmentsByProjectId(projectId);
    if (response && response.status === 200 && response.assignments) {
      set({ projectProcedureInstanceAssignments: response.assignments });
    }
  },
  createProjectAssignment: async (projectId: string, procedureInstanceId: string) => {
    const response = await new ProjectProcedureInstanceAssignmentController().createAssignment({ projectId, procedureInstanceId });
    if (response && response.status === 201 && response.assignment) {
      set({
        projectProcedureInstanceAssignments: [...get().projectProcedureInstanceAssignments, response.assignment]
      });
    }
  },
  deleteProjectAssignment: async (projectId: string, assignmentId: string) => {
    const response = await new ProjectProcedureInstanceAssignmentController().deleteAssignment(assignmentId);
    if (response && response.status === 200) {
      set({
        projectProcedureInstanceAssignments: get().projectProcedureInstanceAssignments.filter(a => a.id !== assignmentId)
      });
    }
  },
  // Mirrors the database cascade: once a procedure instance is deleted its
  // project assignments are gone, so stop holding onto them in the store.
  removeAssignmentsForProcedure: (procedureInstanceId: string) => {
    const assignments = get().projectProcedureInstanceAssignments;
    const remaining = assignments.filter(a => a.procedureInstanceId !== procedureInstanceId);
    if (remaining.length !== assignments.length) {
      set({ projectProcedureInstanceAssignments: remaining });
    }
  },
}));
